import React, { useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Trash2, Settings, ArrowUpCircle, ArrowDownCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Item, ItemStatus } from '@/types';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';
import { useLocationStore } from '@/stores/locationStore';
import { useAuthStore } from '@/stores/authStore';
import ItemDetailModal from './ItemDetailModal';
import ItemForm from './ItemForm';
import CategoryManagementModal from './CategoryManagementModal';
import DirectCheckoutModal from './DirectCheckoutModal';
import RestockModal from './RestockModal';

interface Category {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  isActive: boolean;
  parent?: Category;
  children?: Category[];
  _count?: {
    items: number;
    children: number;
  };
}

const ItemList: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [, setCategories] = useState<Category[]>([]);
  const [flatCategories, setFlatCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ItemStatus | 'ALL'>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const { selectedLocationId } = useLocationStore();
  const { user } = useAuthStore();

  // Check if user is admin
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, [selectedLocationId]);

  const fetchItems = async () => {
    try {
      let response;
      if (selectedLocationId) {
        // Hole Items aus dem ausgewählten Standort und allen Unterstandorten
        response = await apiService.get<Item[]>(`/items/location/${selectedLocationId}`);
      } else {
        // Hole alle Items
        response = await apiService.get<Item[]>('/items');
      }
      setItems(response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Fehler beim Laden der Items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.get<Category[]>('/categories');
      console.log('ItemList: Loaded categories:', response); // Debug-Log
      
      // Extrahiere alle Kategorien flach aus der Hierarchie für das Dropdown
      const flattenAllCategories = (cats: Category[]): Category[] => {
        const result: Category[] = [];
        const addCategory = (cat: Category) => {
          result.push(cat);
          if (cat.children && cat.children.length > 0) {
            cat.children.forEach(child => addCategory(child));
          }
        };
        cats.forEach(cat => addCategory(cat));
        return result;
      };
      
      const hierarchicalCategories = response || [];
      const flatCategories = flattenAllCategories(hierarchicalCategories);
      
      console.log('ItemList: Hierarchical categories:', hierarchicalCategories);
      console.log('ItemList: Flat categories for dropdown:', flatCategories);
      
      // Speichere sowohl hierarchische als auch flache Struktur
      setCategories(hierarchicalCategories);
      setFlatCategories(flatCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const getStatusBadge = (status: ItemStatus) => {
    const statusConfig = {
      [ItemStatus.AVAILABLE]: { variant: 'default' as const, label: 'Verfügbar' },
      [ItemStatus.CHECKED_OUT]: { variant: 'secondary' as const, label: 'Ausgeliehen' },
      [ItemStatus.MAINTENANCE]: { variant: 'destructive' as const, label: 'Wartung' },
      [ItemStatus.RETIRED]: { variant: 'outline' as const, label: 'Außer Betrieb' },
    };

    const config = statusConfig[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Hilfsfunktion: Alle Unterkategorie-IDs einer Kategorie finden
  const getAllSubcategoryIds = (categoryId: string): string[] => {
    const subcategoryIds = [categoryId]; // Inkludiere die Kategorie selbst
    
    const findChildren = (parentId: string) => {
      flatCategories.forEach(category => {
        if (category.parentId === parentId) {
          subcategoryIds.push(category.id);
          findChildren(category.id); // Rekursiv für weitere Unterkategorien
        }
      });
    };
    
    findChildren(categoryId);
    return subcategoryIds;
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'ALL' || item.status === statusFilter;
    
    // Erweiterte Kategorie-Filter-Logik
    let matchesCategory = false;
    if (categoryFilter === 'ALL') {
      matchesCategory = true;
    } else if (categoryFilter === 'NONE') {
      matchesCategory = !item.categoryId;
    } else {
      // Prüfe ob das Item in der ausgewählten Kategorie oder einer ihrer Unterkategorien ist
      const allowedCategoryIds = getAllSubcategoryIds(categoryFilter);
      matchesCategory = item.categoryId ? allowedCategoryIds.includes(item.categoryId) : false;
    }
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Sortiere Items nach Kategorien wenn alle Kategorien angezeigt werden
  const sortedItems = categoryFilter === 'ALL' 
    ? [...filteredItems].sort((a, b) => {
        const categoryA = a.category?.name || 'Zzz_Ohne Kategorie';
        const categoryB = b.category?.name || 'Zzz_Ohne Kategorie';
        return categoryA.localeCompare(categoryB);
      })
    : filteredItems;

  // Funktion zum Rendern der Kategorie-Optionen basierend auf der flachen Liste
  const renderCategoryOptions = (): React.ReactNode[] => {
    // Erstelle eine Map für Parent-Zuordnung
    const categoryMap = new Map<string, Category>();
    flatCategories.forEach(cat => categoryMap.set(cat.id, cat));
    
    // Berechne die Hierarchie-Tiefe für jede Kategorie
    const getCategoryLevel = (categoryId: string): number => {
      const category = categoryMap.get(categoryId);
      if (!category?.parentId) return 0;
      return getCategoryLevel(category.parentId) + 1;
    };
    
    // Sortiere Kategorien hierarchisch
    const sortedCategories = [...flatCategories].sort((a, b) => {
      getCategoryLevel(a.id);
      getCategoryLevel(b.id);
      
      // Erst nach Hierarchie-Pfad, dann alphabetisch
      const getHierarchyPath = (categoryId: string): string => {
        const category = categoryMap.get(categoryId);
        if (!category?.parentId) return category?.name || '';
        return getHierarchyPath(category.parentId) + '/' + category.name;
      };
      
      return getHierarchyPath(a.id).localeCompare(getHierarchyPath(b.id));
    });
    
    return sortedCategories.map(category => {
      const level = getCategoryLevel(category.id);
      const prefix = '  '.repeat(level) + (level > 0 ? '└─ ' : '');
      
      return (
        <option key={category.id} value={category.id}>
          {prefix + category.name}
        </option>
      );
    });
  };

  const handleViewItem = (item: Item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const handleEditItem = (item: Item) => {
    setEditingItem(item);
    setShowFormModal(true);
  };

  const handleDeleteItem = async (item: Item) => {
    if (!confirm(`Möchten Sie "${item.name}" wirklich löschen?`)) {
      return;
    }

    try {
      await apiService.delete(`/items/${item.id}`);
      toast.success('Item erfolgreich gelöscht');
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Fehler beim Löschen des Items');
    }
  };

  const handleCreateItem = () => {
    setEditingItem(null);
    setShowFormModal(true);
  };

  const handleSaveItem = (_savedItem: Item) => {
    fetchItems();
    setShowFormModal(false);
    setEditingItem(null);
  };



  const handleDirectCheckout = (item: Item) => {
    if (!isAdmin) return;
    setSelectedItem(item);
    setShowCheckoutModal(true);
  };

  const handleRestock = (item: Item) => {
    if (!isAdmin) return;
    setSelectedItem(item);
    setShowRestockModal(true);
  };

  const handleCheckoutSuccess = () => {
    fetchItems(); // Aktualisiere die Item-Liste
    setShowCheckoutModal(false);
    setSelectedItem(null);
  };

  const handleRestockSuccess = () => {
    fetchItems(); // Aktualisiere die Item-Liste
    setShowRestockModal(false);
    setSelectedItem(null);
  };

  const exportToCSV = () => {
    // Erstelle CSV-Daten mit allen Item-Informationen
    const csvData = sortedItems.map(item => {
      const checkoutUrl = `${window.location.origin}/checkout/${item.id}`;
      const statusText = {
        [ItemStatus.AVAILABLE]: 'Verfügbar',
        [ItemStatus.CHECKED_OUT]: 'Ausgeliehen',
        [ItemStatus.MAINTENANCE]: 'Wartung',
        [ItemStatus.RETIRED]: 'Außer Betrieb'
      }[item.status] || item.status;

      return {
        'ID': item.id,
        'Name': item.name || '',
        'Beschreibung': item.description || '',
        'Material-Nr': item.sku || '',
        'Barcode': item.barcode || '',
        'Seriennummer': item.serialNumber || '',
        'Kategorie': item.category?.name || '',
        'Status': statusText,
        'Menge': item.quantity,
        'Mindestmenge': item.minQuantity,
        'Ist Ausleihe': item.isRental ? 'Ja' : 'Nein',
        'Lagerstandort': item.location?.name || '',
        'Werknummer': item.location?.plantNumber || '',
        'SAP-Verwaltung': item.isManageInSAP ? 'Ja' : 'Nein',
        'SAP-Werknummern': item.sapPlantNumbers?.join(', ') || '',
        'Erstellt am': new Date(item.createdAt).toLocaleString('de-DE'),
        'Aktualisiert am': new Date(item.updatedAt).toLocaleString('de-DE'),
        'Erstellt von': item.createdBy?.name || '',
        'Checkout-Link': checkoutUrl
      };
    });

    // Erstelle CSV-Content
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(';'),
      ...csvData.map(row => 
        headers.map(header => {
          const value = row[header as keyof typeof row] || '';
          // Escape Semikolons und Anführungszeichen in Werten
          const stringValue = String(value).replace(/"/g, '""');
          // Wenn der Wert ein Semikolon, Zeilenumbruch oder Anführungszeichen enthält, in Anführungszeichen setzen
          if (stringValue.includes(';') || stringValue.includes('\n') || stringValue.includes('"')) {
            return `"${stringValue}"`;
          }
          return stringValue;
        }).join(';')
      )
    ].join('\n');

    // Erstelle Blob und Download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM für Excel-Kompatibilität
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventar-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('CSV-Export erfolgreich erstellt');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Inventar ({sortedItems.length})</CardTitle>
              <p className="text-sm text-muted-foreground">
                Verwalten Sie Ihre Inventargegenstände
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={exportToCSV}>
                <Download className="h-4 w-4 mr-2" />
                CSV Export
              </Button>
              {isAdmin && (
                <>
                  <Button variant="outline" onClick={() => setShowCategoryModal(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Kategorienverwaltung
                  </Button>
                  <Button onClick={handleCreateItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Neues Item
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Nach Items suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md bg-background text-sm min-w-[150px]"
            >
              <option value="ALL">Alle Kategorien</option>
              <option value="NONE">Ohne Kategorie</option>
              {/* Hierarchische Anzeige aller Kategorien */}
              {renderCategoryOptions()}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ItemStatus | 'ALL')}
              className="px-3 py-2 border border-input rounded-md bg-background text-sm"
            >
              <option value="ALL">Alle Status</option>
              <option value={ItemStatus.AVAILABLE}>Verfügbar</option>
              <option value={ItemStatus.CHECKED_OUT}>Ausgeliehen</option>
              <option value={ItemStatus.MAINTENANCE}>Wartung</option>
              <option value={ItemStatus.RETIRED}>Außer Betrieb</option>
            </select>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Name</th>
                  <th className="text-left py-3 px-4 font-medium">Mat. Nr</th>
                  <th className="text-left py-3 px-4 font-medium">Kategorie</th>
                  <th className="text-left py-3 px-4 font-medium">Status</th>
                  <th className="text-left py-3 px-4 font-medium">Menge</th>
                  <th className="text-left py-3 px-4 font-medium">Lagerstandort</th>
                  {isAdmin && <th className="text-left py-3 px-4 font-medium">Warenverw.</th>}
                  <th className="text-left py-3 px-4 font-medium">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium">{item.name}</div>
                        {item.description && (
                          <div className="text-sm text-muted-foreground">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {item.sku || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {item.category?.name ? (
                        <Badge variant="outline">{item.category.name}</Badge>
                      ) : (
                        <span className="text-muted-foreground">Keine Kategorie</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm">
                        <span className="font-medium">{item.quantity}</span>

                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {item.location ? (
                        <>
                          {item.location.name}
                          {item.location.plantNumber && (
                            <span className="text-muted-foreground"> ({item.location.plantNumber})</span>
                          )}
                        </>
                      ) : '-'}
                    </td>
                    {isAdmin && (
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDirectCheckout(item)}
                            title={`Warenausgang - ${item.isRental ? 'Ausleihen' : 'Entnehmen'}`}
                            disabled={item.status !== 'AVAILABLE' || item.quantity === 0}
                          >
                            <ArrowDownCircle className="h-4 w-4 text-red-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRestock(item)}
                            title="Wareneingang"
                          >
                            <ArrowUpCircle className="h-4 w-4 text-green-600" />
                          </Button>
                        </div>
                      </td>
                    )}
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewItem(item)}
                          title="Ansehen"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditItem(item)}
                              title="Bearbeiten"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteItem(item)}
                              title="Löschen"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>

                          </>
                        )}

                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Keine Items gefunden
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <ItemDetailModal
        item={selectedItem}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
      />

      <ItemForm
        item={editingItem}
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSave={handleSaveItem}
      />



      {/* Category Management Modal */}
      <CategoryManagementModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onCategoryChange={() => {
          fetchCategories();
          fetchItems(); // Re-fetch items to get updated category relationships
        }}
      />

      {/* Direct Checkout Modal */}
      <DirectCheckoutModal
        item={selectedItem}
        isOpen={showCheckoutModal}
        onClose={() => {
          setShowCheckoutModal(false);
          setSelectedItem(null);
        }}
        onSuccess={handleCheckoutSuccess}
      />

      {/* Restock Modal */}
      <RestockModal
        item={selectedItem}
        isOpen={showRestockModal}
        onClose={() => {
          setShowRestockModal(false);
          setSelectedItem(null);
        }}
        onSuccess={handleRestockSuccess}
      />
    </div>
  );
};

export default ItemList;
